import { Alert, Button, Group, Modal, PasswordInput, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { errorMessage, isApiError } from './errors';
import { shownError, type Guard } from './reauth';

export interface ChangePasswordDialogProps {
  opened: boolean;
  /** Prompts for a fresh second factor when the server asks for one, then retries. */
  guard: Guard;
  onClose: () => void;
  /** `POST /auth/password` of the app's realm. */
  changePassword: (b: { currentPassword: string; newPassword: string }) => Promise<unknown>;
  /** Runs after a change, to refresh what it touched (sessions, trusted browsers). */
  onChanged?: () => Promise<unknown> | void;
  /** Dark buttons, as on the console. */
  dark?: boolean;
}

/**
 * Changes the user's own password: the current one, then the new one twice
 * (at least 12 characters). The server asks for a fresh second factor first
 * when the user has one; guard prompts for it and retries. Other sessions and
 * trusted browsers are signed out by the change.
 */
export function ChangePasswordDialog({
  opened,
  guard,
  onClose,
  changePassword,
  onChanged,
  dark = false,
}: ChangePasswordDialogProps) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [fieldError, setFieldError] = useState<string>();
  const [error, setError] = useState<unknown>();
  const [pending, setPending] = useState(false);
  const [saved, setSaved] = useState(false);
  const buttonColor = dark ? 'dark' : undefined;

  const close = () => {
    setSaved(false);
    setFieldError(undefined);
    setError(undefined);
    onClose();
  };
  const submit = async () => {
    setFieldError(undefined);
    setError(undefined);
    if ([...next].length < 12) return setFieldError('Use at least 12 characters.');
    if (next !== repeat) return setFieldError('The two passwords do not match.');
    setPending(true);
    try {
      await guard(() => changePassword({ currentPassword: current, newPassword: next }));
    } catch (e) {
      if (isApiError(e, 'invalid') && e.fields?.newPassword)
        setFieldError(`The new password needs ${e.fields.newPassword}.`);
      setError(e);
      return;
    } finally {
      setPending(false);
    }
    setSaved(true);
    setCurrent('');
    setNext('');
    setRepeat('');
    await onChanged?.();
  };
  const wrongCurrent = isApiError(error, 'invalid_credentials');
  const shown = shownError(error);

  return (
    <Modal opened={opened} onClose={close} title="Change your password">
      {saved ? (
        <Stack gap={14}>
          <Text size="sm">
            Password changed. Your other sessions and trusted browsers are signed out.
          </Text>
          <Group justify="flex-end">
            <Button color={buttonColor} onClick={close}>
              Done
            </Button>
          </Group>
        </Stack>
      ) : (
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Stack gap={14}>
            <PasswordInput
              label="Current password"
              autoComplete="current-password"
              data-autofocus
              value={current}
              error={wrongCurrent ? 'Current password not accepted.' : undefined}
              onChange={(e) => setCurrent(e.currentTarget.value)}
            />
            <PasswordInput
              label="New password"
              description="At least 12 characters."
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.currentTarget.value)}
            />
            <PasswordInput
              label="Repeat the new password"
              autoComplete="new-password"
              value={repeat}
              error={fieldError}
              onChange={(e) => setRepeat(e.currentTarget.value)}
            />
            {shown && !wrongCurrent && !fieldError ? (
              <Alert color="red">{errorMessage(shown)}</Alert>
            ) : null}
            <Group justify="flex-end">
              <Button variant="default" onClick={close}>
                Cancel
              </Button>
              <Button type="submit" color={buttonColor} loading={pending}>
                Change password
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
